import React, { useState, useCallback, useMemo, memo, useEffect } from "react";
import { Box, Typography, Skeleton } from "@mui/material";

const defaultGetStyles = (value) => {
  if (value >= 5) return { bg: "#1F36C7B2", color: "#fff" };
  if (value >= 3) return { bg: "#8F9AE3", color: "#000" };
  if (value >= 0) return { bg: "#8F9AE366", color: "#000" };
  return { bg: "#fff", color: "#000" };
};

// Responsive column dimensions per breakpoint (used as floors)
const ROW_HEADER_MIN = { xs: 140, sm: 160, md: 180, lg: 200 };
const ROW_HEADER_MAX = { xs: 200, sm: 220, md: 260, lg: 280 };
const DATA_COL_MIN = { xs: 90, sm: 100, md: 120, lg: 130 };
const DATA_COL_MAX = { xs: 160, sm: 180, md: 200, lg: 220 };

// Approx pixels per character for the heatmap font (~14px) plus cell padding
const CHAR_WIDTH = 7.5;
const CELL_PADDING = 28;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const measureText = (value) =>
  String(value ?? "").length * CHAR_WIDTH + CELL_PADDING;

// Compute dynamic column widths based on longest content per column
const computeColumnWidths = (columns, data, rowHeaderTitle) => {
  const breakpoints = ["xs", "sm", "md", "lg"];

  // Row header column: measure title + all row labels
  const rowHeaderRaw = Math.max(
    measureText(rowHeaderTitle),
    ...data.map((r) => measureText(r.row)),
    0,
  );

  // Each data column: measure column header + all values in that column
  const dataColRaw = columns.map((col) =>
    Math.max(
      measureText(col),
      ...data.map((r) =>
        measureText(
          typeof r.values?.[col] === "number"
            ? r.values[col].toLocaleString()
            : r.values?.[col],
        ),
      ),
      0,
    ),
  );

  // Clamp per breakpoint and assemble responsive objects
  const rowHeaderWidth = breakpoints.reduce((acc, bp) => {
    acc[bp] = clamp(rowHeaderRaw, ROW_HEADER_MIN[bp], ROW_HEADER_MAX[bp]);
    return acc;
  }, {});

  const dataColWidths = breakpoints.reduce((acc, bp) => {
    acc[bp] = dataColRaw.map((w) =>
      clamp(w, DATA_COL_MIN[bp], DATA_COL_MAX[bp]),
    );
    return acc;
  }, {});

  return { rowHeaderWidth, dataColWidths };
};

// Build a responsive gridTemplateColumns object using the dynamic widths.
// Each data column uses `minmax(<measured>px, 1fr)` so it auto-grows when
// the container has extra horizontal space, but never shrinks below content.
const buildGridTemplate = (rowHeaderWidth, dataColWidths) => {
  const breakpoints = ["xs", "sm", "md", "lg"];
  return breakpoints.reduce((acc, bp) => {
    const dataCols = dataColWidths[bp]
      .map((w) => `minmax(${w}px, 1fr)`)
      .join(" ");
    acc[bp] = `${rowHeaderWidth[bp]}px ${dataCols}`;
    return acc;
  }, {});
};

// Total min width of the inner grid container — also responsive
const buildGridMinWidth = (rowHeaderWidth, dataColWidths) => {
  const breakpoints = ["xs", "sm", "md", "lg"];
  return breakpoints.reduce((acc, bp) => {
    acc[bp] =
      rowHeaderWidth[bp] + dataColWidths[bp].reduce((sum, w) => sum + w, 0);
    return acc;
  }, {});
};

const CustomTableHeader = memo(({ columns, rowHeaderTitle, gridTemplate }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: gridTemplate,
      minHeight: { xs: 52, sm: 56, md: 64, lg: 68 },
      position: "sticky",
      top: 0,
      zIndex: 3,
    }}
  >
    <Box
      sx={{
        px: { xs: 1.25, md: 2 },
        py: { xs: 1, md: 1.5 },
        bgcolor: "#354ACD",
        color: "#fff",
        position: "sticky",
        left: 0,
        borderRight: "1px solid #E0E0E0",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        zIndex: 4,
      }}
    >
      <Typography
        fontWeight={600}
        sx={{
          fontSize: { xs: 11, sm: 12, md: 13, lg: 14 },
          wordBreak: "break-word",
          lineHeight: 1.3,
        }}
      >
        {rowHeaderTitle}
      </Typography>
    </Box>
    {columns.map((col) => (
      <Box
        key={col}
        sx={{
          px: { xs: 1, md: 2 },
          py: { xs: 1, md: 1.5 },
          textAlign: "center",
          bgcolor: "#354ACD",
          color: "#fff",
          borderRight: "1px solid #E0E0E0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          fontWeight={600}
          sx={{
            fontSize: { xs: 11, sm: 12, md: 13, lg: 14 },
            wordBreak: "break-word",
            lineHeight: 1.3,
          }}
        >
          {col}
        </Typography>
      </Box>
    ))}
  </Box>
));

const CustomSkeletonRows = memo(({ columns, count = 5, gridTemplate }) => (
  <>
    {Array.from({ length: count }).map((_, rowIndex) => (
      <Box
        key={rowIndex}
        sx={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
        }}
      >
        <Box
          sx={{
            p: { xs: 1.25, md: 2 },
            borderRight: "1px solid #E0E0E0",
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <Skeleton width={100} height={20} />
        </Box>
        {columns.map((_, colIndex) => (
          <Box
            key={colIndex}
            sx={{
              p: { xs: 0.5, md: 1 },
              borderRight: "1px solid #E0E0E0",
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <Skeleton
              variant="rounded"
              sx={{ height: { xs: 28, md: 32, lg: 36 } }}
            />
          </Box>
        ))}
      </Box>
    ))}
  </>
));
const CustomTableRows = memo(
  ({ tableData, columns, activeView, styleFn, gridTemplate }) => (
    <>
      {tableData.map((rowData, index) => (
        <Box
          key={index}
          sx={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            minHeight: { xs: 52, sm: 56, md: 64, lg: 68 },
          }}
        >
          {/* ROW HEADER */}
          <Box
            sx={{
              p: { xs: 1.25, md: 2 },
              bgcolor: "#fff",
              position: "sticky",
              left: 0,
              borderRight: "1px solid #E0E0E0",
              borderBottom: "1px solid #E0E0E0",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 11, sm: 12, md: 13, lg: 14 },
              }}
            >
              {rowData.row}
            </Typography>
          </Box>

          {/* DATA CELLS */}
          {columns.map((col) => {
            const value = rowData.values?.[col] ?? "-";
            const styles =
              activeView === "graph" && value !== "-" ? styleFn(value) : null;

            return (
              <Box
                key={col}
                sx={{
                  p: { xs: 0.5, md: 1 },
                  borderRight: "1px solid #E0E0E0",
                  borderBottom: "1px solid #E0E0E0",
                }}
              >
                <Box
                  sx={{
                    py: { xs: 1, md: 1.5 },
                    borderRadius: 1,
                    textAlign: "center",
                    fontWeight: 600,
                    fontSize: { xs: 11, sm: 12, md: 13, lg: 14 },
                    ...(styles && {
                      bgcolor: styles.bg,
                      color: styles.color,
                    }),
                  }}
                >
                  {typeof value === "number" ? value.toLocaleString() : value}
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </>
  ),
);
const CustomTableContent = memo(
  ({
    columns,
    rowHeaderTitle,
    loading,
    tableData,
    activeView,
    styleFn,
    gridTemplate,
    gridMinWidth,
  }) => (
    <Box
      sx={{ borderRadius: 3, border: "1px solid #E0E0E0", overflow: "hidden" }}
    >
      <Box
        sx={{
          maxHeight: { sm: 160, md: 200, lg: 300 },
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <Box sx={{ minWidth: gridMinWidth }}>
          <CustomTableHeader
            columns={columns}
            rowHeaderTitle={rowHeaderTitle}
            gridTemplate={gridTemplate}
          />
          {loading ? (
            <CustomSkeletonRows columns={columns} gridTemplate={gridTemplate} />
          ) : (
            <CustomTableRows
              tableData={tableData}
              columns={columns}
              activeView={activeView}
              styleFn={styleFn}
              gridTemplate={gridTemplate}
            />
          )}
        </Box>
      </Box>
    </Box>
  ),
);



const HeatmapTable = ({
  lastRefreshed,
  icons = {},
  data = [],
  columns = [],
  getStyles,
  rowHeaderTitle = "",
  refreshTrigger,
}) => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState(data);
  const [setRefreshTime] = useState(lastRefreshed);
  const [activeView] = useState("graph");

  const styleFn = useMemo(() => getStyles || defaultGetStyles, [getStyles]);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      setTableData([...data]);
      setRefreshTime(`Last refreshed at ${new Date().toLocaleTimeString()}`);
      setLoading(false);
    }, 1500);
  }, [data]);
  useEffect(() => {
    if (refreshTrigger) {
      handleRefresh();
    }
  }, [refreshTrigger, handleRefresh]);

  // Dynamically compute column widths based on actual content + container.
  // Recomputes when data, columns, or row header label change.
  const { gridTemplate, gridMinWidth } = useMemo(() => {
    const { rowHeaderWidth, dataColWidths } = computeColumnWidths(
      columns,
      tableData,
      rowHeaderTitle,
    );
    return {
      gridTemplate: buildGridTemplate(rowHeaderWidth, dataColWidths),
      gridMinWidth: buildGridMinWidth(rowHeaderWidth, dataColWidths),
    };
  }, [columns, tableData, rowHeaderTitle]);

  const tableProps = {
    columns,
    rowHeaderTitle,
    loading,
    tableData,
    activeView,
    styleFn,
    gridTemplate,
    gridMinWidth,
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 4,
        border: "1px solid #DFDFDF",
        height: "100%",
      }}
    >
      <CustomTableContent {...tableProps} />
    </Box>
  );
};;

export default HeatmapTable;
